import type { CopywritingCampaign } from "../../../../../pages/ListEditCopywriting/ListEditCopywriting";
import type { ListPerson } from "../../../../../schema/list";

/**
 * The campaign a lead card shows for a lead: its own custom sequence, the list campaign it is
 * assigned to, a campaign matching its language, or the list default — in that order.
 *
 * The three outcomes are read by {@link getCampaignTagState}: `undefined` when the list has no
 * campaign config at all, `null` when it has campaigns but none is valid or none resolves for
 * this lead, and the campaign otherwise. Extracted from `Lead.tsx` so the compact Review-to-send
 * card (PREC-4175) decides the tag exactly as the list-row card does.
 */
export const resolveLeadCampaignConfig = (
	row: ListPerson | null | undefined
): CopywritingCampaign | undefined | null => {
	if (!row) {
		return undefined;
	}

	if (row.campaignConfig) {
		return row.campaignConfig as CopywritingCampaign;
	}

	const allCampaigns = row.list.campaignConfig as { [key: string]: CopywritingCampaign };

	if (!allCampaigns) {
		return undefined;
	}

	const allValidCampaigns = Object.keys(allCampaigns)?.filter(
		(campaign) => row.list.campaignConfig[campaign].isValid
	);

	if (!allValidCampaigns || allValidCampaigns.length === 0) {
		return null;
	}

	if (row.campaignId && row.list.campaignConfig[row.campaignId]) {
		return row.list.campaignConfig[row.campaignId] as CopywritingCampaign;
	}

	const defaultCampaignId = allValidCampaigns.find(
		(campaign) => row.list.campaignConfig[campaign].isDefault
	);

	const languages = row.prospect?.language
		?.split(",")
		?.map((lang: string) => lang.trim().toLowerCase());

	const languageBasedDefaultCampaignId = allValidCampaigns.find((campaign) =>
		row?.list?.campaignConfig?.[campaign]?.languages?.some((lang: string) =>
			languages?.includes(lang.toLowerCase())
		)
	);

	const languageBasedDefaultCampaign = languageBasedDefaultCampaignId
		? ({
				...(allCampaigns[languageBasedDefaultCampaignId] || {}),
				id: languageBasedDefaultCampaignId,
			} as CopywritingCampaign)
		: null;

	// Only return a default campaign if it has a valid ID
	const defaultCampaign = defaultCampaignId
		? ({
				...(allCampaigns[defaultCampaignId] || {}),
				id: defaultCampaignId,
			} as CopywritingCampaign)
		: null;

	return languageBasedDefaultCampaign || defaultCampaign || null;
};
